import React, { useState, useRef, useCallback } from 'react';
import { connect } from 'redux-zero/react';
import { useTranslation } from 'react-i18next';

import {
  DsButton,
  DsDialog,
  DsExplorerLink,
  DsIcon,
  DsTooltip,
} from '@tokenyico/design-system/dist/reactJS';
import { datetimeFormat, dsConfirm } from '@tokenyico/design-system';

import { formActions } from 'app/state/actions';
import { SubscriptionsService } from 'services';

import { useOrdersTableHeader } from './useOrdersTableHeader';
import SubscriptionStatusType from './SubscriptionStatusType';
import { TotalPayment } from '../helpers';
import { displayCurrency } from 'app/helpers/currency';
import TableWrapper from 'app/components/TableWrapper/TableWrapper';
import OrdersWrapper from '../OrdersWrapper';
import TokenAmountCell from 'app/components/Cells/TokenAmountCell';

import './SubscriptionsOrders.less';
import OrderDetailsTabView from 'app/components/Orders/OrderDetailsTabView';
import SubscriptionPaymentInfoView from './SubscriptionPaymentInfoView';
import SubscriptionOrderDetailsView from './SubscriptionOrderDetailsView';

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <div className="empty-state">
      <div className="empty-state__title">{t('emptyState.noSubscriptionsOrders.title')}</div>
      <div className="empty-state__body">{t('emptyState.noSubscriptionsOrders.body')}</div>
    </div>
  );
};

const SubscriptionsOrdersView = ({
  setSuccess,
  setError,
  history,
  holder: { selectedToken, redemption },
}) => {
  const { t } = useTranslation();

  const detailDialogRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [actionLoadingRow, setActionLoadingRow] = useState({});
  const [totalFoundRows, setTotalFoundRows] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [withDocusign, setWithDocusign] = useState(false);
  const [isOpenEnded, setIsOpenEnded] = useState(false);
  const [paymentMethodsTypes, setPaymentMethodsTypes] = useState([]);
  const [isOrderOpen, setOrderOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [subscription, setSubscription] = useState({});
  const [tableState, setTableState] = useState({});

  const header = useOrdersTableHeader({ withDocusign, isOpenEnded, paymentMethodsTypes });

  const handleShowOrder = async (e, row) => {
    setSelectedOrder(row);
    setOrderOpen(true);
    detailDialogRef.current.open();
  };

  const loadData = useCallback(
    async (detail) => {
      try {
        const query = {
          limit: detail.rowsPerPage,
          offset: (detail.currentPage - 1) * detail.rowsPerPage,
          /* currently not supporting filtering and sorting */
          // filter: detail.filter,
          // sortBy: detail.sortBy,
          // sortDirection: detail.sortDirection,
        };
        setTableState(detail);
        const subscriptions = await SubscriptionsService.getSubscriptions(selectedToken.id);
        if (subscriptions.items.length > 0) {
          const [subscription] = subscriptions.items;
          setWithDocusign(subscription.supportingDocumentsRequired);
          setSubscription(subscription);
          const [intervals] = subscription.intervals;
          setIsOpenEnded(intervals.type === 'OPEN_ENDED');
          const uniquePaymentMethods = [
            ...new Set(
              Object.values(subscription.paymentMethods).map((pm) => pm.type.toLowerCase()),
            ),
          ];
          setPaymentMethodsTypes(uniquePaymentMethods);
          let data = await SubscriptionsService.getSubscriptionOrders({
            ...query,
            tokenId: selectedToken.id,
            subscriptionId: subscription.id,
          });
          setTableData(data.items);
          setTotalRows(data.totalItemsFound);
          setSubscriptionId(subscription.id);
          if (totalFoundRows === 0) setTotalFoundRows(data.totalItemsFound);
        }
      } catch (e) {
        setError(e);
      }
    },
    [setError, totalFoundRows],
  );

  /* end table */

  const handleCancelOrder = async (order) => {
    setActionLoadingRow(order);
    try {
      if (
        await dsConfirm({
          heading: `${t(`shared.cancel`)} ${t('shared.subscriptionsOrders').toLocaleLowerCase()}`,
          content: `${t('shared.subscriptionsOrdersDialogDescription', {
            action: t(`shared.cancel`).toLocaleLowerCase(),
          })}
          <br/> <strong>${order.id}</strong>`,
          action: t(`shared.confirm`),
          version: 'danger',
        })
      ) {
        await SubscriptionsService.postSubscriptionOrdersAction({
          action: 'cancel',
          subscriptionId,
          tokenId: selectedToken.id,
          orderId: order.id,
        });

        setSuccess(t('shared.subscriptionsOrdersDialogSuccess'));
        //reload
        await loadData(tableState);
      }
      setActionLoadingRow({});
    } catch (e) {
      setError(e);
      setActionLoadingRow({});
    }
  };

  return (
    <OrdersWrapper currentTab={0} hasRedemption={!!redemption}>
      <TableWrapper
        height="100%"
        width="100%"
        header={header}
        data={tableData}
        totalRows={totalRows}
        rowsPerPage={10}
        onFetchData={loadData}
        noData={() => <EmptyState subscriptionId={subscriptionId} history={history} />}
        // rendering functions
        viewDetails={({ row }) => (
          <DsButton
            mode="text"
            icon="view"
            iconSize="1rem"
            onClick={(e) => handleShowOrder(e, row)}
            class="_p(0)"
          />
        )}
        actions={({ row }) =>
          row.status === 'PENDING_PAYMENT' ? (
            <DsButton
              icon="cross-circle"
              iconSize="16px"
              isMini
              version="error"
              onClick={() => handleCancelOrder(row)}
              isLoading={row.id === actionLoadingRow.id}
            />
          ) : null
        }
        createdAt={({ value }) => datetimeFormat(value)}
        status={({ value }) => <SubscriptionStatusType status={value} />}
        amountToPay={({ row }) => (
          <TotalPayment
            payments={{ investment: row.investmentAmountAtCreation, fee: row.feeAtCreation }}
            currency={row.investmentCurrency}
          />
        )}
        tokensOrdered={({ row }) =>
          ['CANCELED_BY_INVESTOR', 'CANCELED_BY_ISSUER'].includes(row.status) ? (
            '-'
          ) : row.tokensAtCreation ? (
            <TokenAmountCell amount={row.tokensAtCreation} />
          ) : isOpenEnded ? (
            <DsTooltip text={t('shared.tokensOrderedTooltip')} theme="light">
              <DsIcon name="help" color="var(--warning)" />
            </DsTooltip>
          ) : (
            '-'
          )
        }
        confirmedPayment={({ row, column }) => (
          <TotalPayment
            payments={{
              investment: row.investmentAmountAtPayment,
              fee: row.feeAtPayment,
            }}
            currency={row.investmentCurrency}
            column={column}
          />
        )}
        tokensConfirmed={({ row }) =>
          ['CANCELED_BY_INVESTOR', 'CANCELED_BY_ISSUER'].includes(row.status) ? (
            '-'
          ) : row.tokensAtPayment ? (
            <TokenAmountCell amount={row.tokensAtPayment} />
          ) : isOpenEnded && !row.tokenPrice ? (
            <DsTooltip text={t('shared.tokensConfirmedTooltip')} theme="light">
              <DsIcon name="help" color="var(--warning)" />
            </DsTooltip>
          ) : (
            '-'
          )
        }
        tokenPrice={({ row, value }) =>
          ['CANCELED_BY_INVESTOR', 'CANCELED_BY_ISSUER'].includes(row.status) ? (
            '-'
          ) : value ? (
            displayCurrency(value, row.baseCurrency)
          ) : isOpenEnded ? (
            <DsTooltip text={t('shared.tokenPriceTooltip')} theme="light">
              <DsIcon name="help" color="var(--warning)" />
            </DsTooltip>
          ) : (
            '-'
          )
        }
        tokenPriceTime={({ row }) =>
          ['CANCELED_BY_INVESTOR', 'CANCELED_BY_ISSUER'].includes(row.status) ? (
            '-'
          ) : row.tokenPriceAt ? (
            datetimeFormat(row.tokenPriceAt)
          ) : isOpenEnded ? (
            <DsIcon name="pending" color="var(--warning)" />
          ) : null
        }
        paymentReference={({ value }) => value || '-'}
        txHash={({ row, value }) =>
          value ? (
            <DsExplorerLink
              address={value}
              network={row.paymentNetwork}
              type="transaction"
              width="10rem"
            />
          ) : (
            '-'
          )
        }
      />
      <DsDialog
        ref={detailDialogRef}
        isModal={true}
        isDismissible={true}
        onClosed={() => setOrderOpen(false)}
        id="subscriptionOrderDialog"
      >
        <div>
          {isOrderOpen ? (
            <OrderDetailsTabView
              order={selectedOrder}
              onCloseModal={() => detailDialogRef.current.close()}
              actions={
                selectedOrder.status === 'PENDING_PAYMENT' ? (
                  <DsButton
                    label={t('shared.cancelOrder')}
                    version="error"
                    onClick={() => {
                      handleCancelOrder(selectedOrder);
                      detailDialogRef.current.close();
                    }}
                  />
                ) : null
              }
            >
              {/* add title props each child to define tab title */}
              <SubscriptionOrderDetailsView
                title={t('shared.orderDetails')}
                order={selectedOrder}
                subscription={subscription}
              />
              <SubscriptionPaymentInfoView
                title={t('shared.paymentInfo')}
                order={selectedOrder}
                subscription={subscription}
              />
            </OrderDetailsTabView>
          ) : null}
        </div>
      </DsDialog>
    </OrdersWrapper>
  );
};

const mapToProps = (state) => state;

export default connect(mapToProps, formActions)(SubscriptionsOrdersView);
